import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaBars, FaTimes, FaChevronRight, FaChevronDown, FaKey, FaSignOutAlt } from "react-icons/fa";
import { logoutUser, selectUser } from "../../features/userSlice";
import logo from "../../assets/grouplogo.png";
import "./Header.css";

const Header = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [menu, setMenu] = useState([]);
  const [userName, setUserName] = useState(user?.Name || "");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});

  const organizeMenuData = (menuData) => {
    const menuMap = {};
    const rootMenu = [];

    menuData.forEach((menuItem) => {
      menuMap[menuItem.menu_id] = { ...menuItem, children: [] };
    });

    menuData.forEach((menuItem) => {
      if (menuItem.parent_menu_id) {
        menuMap[menuItem.parent_menu_id]?.children.push(menuMap[menuItem.menu_id]);
      } else {
        rootMenu.push(menuMap[menuItem.menu_id]);
      }
    });

    return rootMenu;
  };

  useEffect(() => {
    setUserName(user?.Name || "");

    if (!user?.emp_id) {
      setMenu([]);
      setDrawerOpen(false);
      return undefined;
    }

    let cancelled = false;

    const loadMenu = async () => {
      try {
        const response = await axios.post("/api/menu/getMenuUserData", {
          emp_id: user.emp_id,
        });
        if (!cancelled) {
          setMenu(organizeMenuData(response.data || []));
        }
      } catch (error) {
        console.log("fetchmenu error:", error);
        if (!cancelled) setMenu([]);
      }
    };

    loadMenu();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!drawerOpen) {
      setExpandedIds({});
      return undefined;
    }

    const onKeyDown = (e) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const handleLogout = () => {
    setDrawerOpen(false);
    dispatch(logoutUser());
    setUserName("");
    setMenu([]);
    navigate("/");
  };

  const handleChangePassword = () => {
    setDrawerOpen(false);
    navigate("/change-password");
  };

  const handleMenuClick = (menuItem) => {
    if (menuItem.menu_url) {
      setDrawerOpen(false);
      navigate(menuItem.menu_url);
    } else if (menuItem.handler) {
      setDrawerOpen(false);
      menuItem.handler();
    }
  };

  const toggleExpand = (menuId, siblingIds) => {
    setExpandedIds((prev) => {
      if (prev[menuId]) {
        const next = { ...prev };
        delete next[menuId];
        return next;
      }

      const next = { ...prev };
      siblingIds.forEach((id) => {
        if (id !== menuId) delete next[id];
      });
      next[menuId] = true;
      return next;
    });
  };

  const renderDrawerMenu = (menuItems, depth = 0) => {
    const siblingIds = menuItems.map((item) => item.menu_id);

    return menuItems.map((menuItem) => {
      const hasChildren = menuItem.children?.length > 0;
      const isExpanded = !!expandedIds[menuItem.menu_id];

      if (hasChildren) {
        return (
          <li key={menuItem.menu_id} className="app-header-nav-group">
            <button
              type="button"
              className={`app-header-nav-item app-header-nav-parent depth-${depth}${
                isExpanded ? " is-open" : ""
              }`}
              onClick={() => toggleExpand(menuItem.menu_id, siblingIds)}
              aria-expanded={isExpanded}
            >
              <span>{menuItem.menu_name}</span>
              {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
            </button>
            {isExpanded && (
              <ul className="app-header-nav-children">
                {renderDrawerMenu(menuItem.children, depth + 1)}
              </ul>
            )}
          </li>
        );
      }

      return (
        <li key={menuItem.menu_id}>
          <button
            type="button"
            className={`app-header-nav-item depth-${depth}`}
            onClick={() => handleMenuClick(menuItem)}
          >
            {menuItem.menu_name}
          </button>
        </li>
      );
    });
  };

  const initials = userName
    ? userName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("")
    : "";

  if (!user) {
    return null;
  }

  return (
    <header className="app-header">
      <div className="app-header-bar">
        <button
          type="button"
          className="app-header-brand"
          onClick={() => navigate("/dashboard")}
          aria-label="Go to home"
        >
          <img src={logo} alt="WB Auto" className="app-header-logo" />
        </button>

        <div className="app-header-actions">
          <div className="app-header-user" title={userName}>
            <span className="app-header-avatar" aria-hidden="true">
              {initials || "U"}
            </span>
            <span className="app-header-user-text">
              <span className="app-header-username">Hii {userName}</span>
            </span>
          </div>

          <button
            type="button"
            className="app-header-menu-btn"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
          >
            <FaBars />
          </button>
        </div>
      </div>

      <>
          <div
            className={`app-header-backdrop ${drawerOpen ? "is-open" : ""}`}
            onClick={() => setDrawerOpen(false)}
            aria-hidden={!drawerOpen}
          />
          <aside
            className={`app-header-drawer ${drawerOpen ? "is-open" : ""}`}
            aria-hidden={!drawerOpen}
            aria-label="Navigation menu"
          >
            <div className="app-header-drawer-top">
              <div className="app-header-drawer-user">
                <span className="app-header-avatar lg" aria-hidden="true">
                  {initials || "U"}
                </span>
                <div>
                  <p className="app-header-drawer-name">{userName}</p>
                </div>
              </div>
              <button
                type="button"
                className="app-header-icon-btn on-dark"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
              >
                <FaTimes />
              </button>
            </div>

            <nav className="app-header-drawer-nav">
              <p className="app-header-drawer-label">Menu</p>
              <ul className="app-header-nav-list">
                {menu.length > 0 ? (
                  renderDrawerMenu(menu)
                ) : (
                  <li className="app-header-nav-empty">No menu items available</li>
                )}
              </ul>
            </nav>

            <div className="app-header-drawer-footer">
              <button type="button" className="app-header-drawer-action" onClick={handleChangePassword}>
                <FaKey /> Change Password
              </button>
              <button type="button" className="app-header-drawer-action danger" onClick={handleLogout}>
                <FaSignOutAlt /> Logout
              </button>
            </div>
          </aside>
      </>
    </header>
  );
};

export default Header;
